import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { getELockModuleKey } from 'sections/LKSection/modules/ELockModule/utils'
import { getOrderFilesStepKey } from 'sections/LKSection/modules/OrderModule/utils'
import { UploadFile } from 'components/ui/UploadFile/UploadFile'
import { Flex, Input, Modal, Select, TextArea } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { WEB_LINK_REGEXP } from 'shared/constants/regExp'

import {
  initialTicketFileValue,
  MIN_DESCRIPTION_LENGTH,
  ticketFileValidate,
  TicketKeys,
  ticketPhotoValidate,
  ticketReasonOptions,
  ticketTypeOptions,
} from '../../data'
import { TicketPhotoType, TicketReason, TicketType } from '../../types'
import { validateTicketForm } from './validate'

type CreateTicketModalProps = {
  open: boolean
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

type TicketFormType = {
  description: string
  link: string
}

type DeleteTicketFileType = { index?: number; fileType?: TicketPhotoType }

const getKey = (key: string) => `${TicketKeys.createTicket}.${key}`

export const CreateTicketModal = observer(({ open, setIsDeleteModalOpen }: CreateTicketModalProps) => {
  const { createTicketUiStore: uiStore, viewOrderUiStore, viewTicketUiStore } = useStore()

  const [form] = Form.useForm()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (viewOrderUiStore.selectedOrder?.id) {
      uiStore.setSelectedOrderId(viewOrderUiStore.selectedOrder.id)
    }
  }, [viewOrderUiStore.selectedOrder])

  useEffect(() => {
    uiStore.reset()
    form.resetFields()
  }, [uiStore.ticketTypeValue])

  const createTicketHandler = async () => {
    if (validateTicketForm.validate(uiStore)) {
      setLoading(true)

      const ticketCreated = await uiStore.createTicket()

      if (ticketCreated) {
        setIsDeleteModalOpen(false)
        uiStore.reset()
        form.resetFields()
        void viewTicketUiStore.getTicketList()
      }

      setLoading(false)
    }
  }

  const deleteFileHandler = ({ index = 0, fileType = TicketPhotoType.generalPhoto }: DeleteTicketFileType) => {
    if (uiStore.ticketTypeValue === TicketType.openingOrder) {
      const newFiles = [...uiStore.files]
      newFiles.splice(index, 1)
      uiStore.setFiles(newFiles)
    } else {
      uiStore.setFileData(fileType)(initialTicketFileValue)
    }
  }

  return (
    <Modal
      title={t(getKey('Создание тикета'))}
      open={open}
      okButtonProps={{ loading }}
      okText={t(getKey('Создать тикет'))}
      cancelText={t(getSharedKey('cancel'))}
      onOk={createTicketHandler}
      onCancel={() => setIsDeleteModalOpen(false)}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item label={t(getKey('Тема тикета'))}>
          <Select
            value={uiStore.ticketTypeValue}
            onChange={(value: TicketType) => uiStore.setTicketTypeValue(value)}
            options={ticketTypeOptions}
            placeholder={t(getKey('Выберите тему тикета'))}
          />
        </Form.Item>

        <Form.Item label={t(getKey('Выбор заявки'))}>
          <Select
            value={uiStore.selectedOrderId}
            onChange={(value: number) => uiStore.setSelectedOrderId(value)}
            options={uiStore.ordersOptions}
            placeholder={t(getKey('Выберите заявку'))}
            showSearch
          />
        </Form.Item>

        {uiStore.ticketTypeValue === TicketType.openingOrder && (
          <Form.Item label={t(getKey('Причина обращения'))}>
            <Select
              value={uiStore.ticketReasonValue}
              onChange={(value: TicketReason) => uiStore.setTicketReasonValue(value)}
              options={ticketReasonOptions}
              placeholder={t(getKey('Выберите причину обращения'))}
            />
          </Form.Item>
        )}

        {uiStore.ticketTypeValue === TicketType.openingOrder && (
          <Form.Item<TicketFormType>
            label={t(getELockModuleKey('Описание проблемы'))}
            name="description"
            rules={[
              { min: MIN_DESCRIPTION_LENGTH, message: t(getSharedKey('minLength'), { count: MIN_DESCRIPTION_LENGTH }) },
              { required: true, message: t(getSharedKey('required')) },
            ]}
          >
            <TextArea
              value={uiStore.description}
              onChange={(e) => uiStore.setDescription(e.target.value)}
              maxLength={500}
              style={{ resize: 'none' }}
              placeholder={`${t(getKey('Опишите проблему'))}...`}
            />
          </Form.Item>
        )}

        <Form.Item<TicketFormType>
          label={`${t(getSharedKey('attachLink'))} (${t(getSharedKey('notRequired'))})`}
          name="link"
          rules={[{ pattern: WEB_LINK_REGEXP, message: t(getKey('URLIncorrectMessage')) }]}
          tooltip={t(getKey('Если проблема с загрузкой в тикетах'))}
        >
          <Input
            value={uiStore.link}
            onChange={(e) => uiStore.setLink(e.target.value)}
            maxLength={500}
            placeholder={t(getKey('Прикрепите ссылку на облачное хранилище'))}
          />
        </Form.Item>

        {uiStore.ticketTypeValue === TicketType.openingOrder && (
          <Form.Item
            label={`${t(getSharedKey('attachFiles'))} (${t(getSharedKey('notRequired'))})`}
            tooltip={t(getKey('При необходимости загрузить файлы'))}
          >
            <Flex gap={8} wrap>
              {Array.from({ length: Math.min(3, uiStore.files.length + 1) }).map((_, index) => (
                <UploadFile
                  key={index}
                  contentUrl={uiStore.files[index]?.content ?? ''}
                  onChange={(file) => uiStore.setFiles([...uiStore.files, file])}
                  onDelete={() => deleteFileHandler({ index })}
                  fileValidationOptions={ticketFileValidate}
                />
              ))}
            </Flex>
          </Form.Item>
        )}

        {uiStore.ticketTypeValue === TicketType.roomLayout && (
          <Form.Item label={t(getSharedKey('attachFiles'))} tooltip={t(getKey('Файлы в хорошем качестве'))}>
            <Form.Item>
              <UploadFile
                label={t(getOrderFilesStepKey('Фасад здания днем'))}
                description={t(getOrderFilesStepKey('Дневное фото фасада здания'))}
                contentUrl={uiStore.generalPhoto?.content ?? ''}
                onChange={(file) => uiStore.setFileData(TicketPhotoType.generalPhoto)(file)}
                onDelete={() => deleteFileHandler({ fileType: TicketPhotoType.generalPhoto })}
                fileValidationOptions={ticketPhotoValidate}
              />
            </Form.Item>

            <Form.Item>
              <UploadFile
                label={t(getOrderFilesStepKey('План БТИ'))}
                description={t(getKey('План БТИ должен быть с отметками'))}
                contentUrl={uiStore.btiDocument?.content ?? ''}
                onChange={(file) => uiStore.setFileData(TicketPhotoType.btiDocument)(file)}
                onDelete={() => deleteFileHandler({ fileType: TicketPhotoType.btiDocument })}
                fileValidationOptions={ticketPhotoValidate}
              />
            </Form.Item>
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
})
